import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  RevitParameter,
  RevitElement,
  RevitFamily,
  RevitSettings,
  RevitConnectorInfo,
} from '../types';
import { getStorage } from '../lib/utils/safeStorage';

// Re-export types for backward compatibility
export type {
  RevitParameter,
  RevitElement,
  RevitFamily,
  RevitConnectorInfo as RevitConnector,
};

// Project settings type alias (use centralized RevitSettings)
export type RevitProjectSettings = RevitSettings;

// ============================================================================
// State Interface
// ============================================================================

interface RevitState {
  // Core data
  parameters: RevitParameter[];
  elements: RevitElement[];
  families: RevitFamily[];
  settings: RevitProjectSettings | null;

  // Loading states
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;

  // Parameter categories for quick filtering
  parameterCategories: string[];
}

// ============================================================================
// Actions Interface
// ============================================================================

interface RevitActions {
  // Data setters
  setParameters: (parameters: RevitParameter[]) => void;
  setElements: (elements: RevitElement[]) => void;
  setFamilies: (families: RevitFamily[]) => void;
  updateSettings: (settings: Partial<RevitProjectSettings>) => void;

  // Loading state actions
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setLoaded: (isLoaded: boolean) => void;

  // Data loading actions
  loadRevitApiData: () => Promise<void>;
  refreshData: () => Promise<void>;

  // Reset
  resetData: () => void;

  // Selectors/Getters
  getParametersByCategory: (category: string) => RevitParameter[];
  getParameterByName: (name: string) => RevitParameter | undefined;
  getElementsByType: (type: string) => RevitElement[];
  getFamiliesByCategory: (category: string) => RevitFamily[];
}

// ============================================================================
// Mock Data Generators (Placeholder for Revit API)
// ============================================================================

/**
 * Placeholder function to simulate fetching parameters from Revit API
 * In production, this would call the actual Revit API via a bridge
 */
async function fetchRevitParameters(): Promise<RevitParameter[]> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 100));

  return [
    // Connection Parameters
    { id: 'p1', name: 'PiX_PPG_END1_TYPE', category: 'Connections', dataType: 'string', description: 'End 1 connection type (Primary connector)', group: 'PipeEX' },
    { id: 'p2', name: 'PiX_PPG_END2_TYPE', category: 'Connections', dataType: 'string', description: 'End 2 connection type (Secondary connector)', group: 'PipeEX' },
    { id: 'p3', name: 'PiX_PPG_END3_TYPE', category: 'Connections', dataType: 'string', description: 'End 3 connection type (Perpendicular)', group: 'PipeEX' },
    { id: 'p4', name: 'PiX_PPG_END4_TYPE', category: 'Connections', dataType: 'string', description: 'End 4 connection type', group: 'PipeEX' },

    // Specification Parameters
    { id: 'p5', name: 'PiP_PPG_SPEC', category: 'Specifications', dataType: 'string', description: 'Piping specification', group: 'PipeEX' },
    { id: 'p6', name: 'PiX_PPG_END1_PressureRating', category: 'Specifications', dataType: 'string', description: 'Pressure rating class', group: 'PipeEX' },

    // Material Parameters
    { id: 'p7', name: 'PiX_03_Material_ENG', category: 'Material', dataType: 'string', description: 'Material specification (English)', group: 'PipeEX' },
    { id: 'p8', name: 'PiX_02_Standard_ENG', category: 'Material', dataType: 'string', description: 'Design standard (English)', group: 'PipeEX' },

    // Insulation Parameters
    { id: 'p9', name: 'PiX_GN_2_InsulationSpec', category: 'Insulation', dataType: 'string', description: 'Insulation type specification', group: 'PipeEX' },
    { id: 'p10', name: 'PiX_GN_2_InsulationThickness', category: 'Insulation', dataType: 'string', description: 'Insulation thickness', group: 'PipeEX' },

    // Tracing Parameters
    { id: 'p11', name: 'PiX_GN_2_HeatTracingSpec', category: 'Tracing', dataType: 'string', description: 'Heat tracing specification', group: 'PipeEX' },

    // Description Parameters
    { id: 'p12', name: 'PiX_01_Description_ENG', category: 'Description', dataType: 'string', description: 'Item description (English)', group: 'PipeEX' },

    // Bolt Parameters
    { id: 'p13', name: 'PiX_PPG_920_BoltSet_ON', category: 'Bolts', dataType: 'boolean', description: 'Bolt set active', group: 'PipeEX' },
    { id: 'p14', name: 'Pix_PPG_914.BoltSet.Description', category: 'Bolts', dataType: 'string', description: 'Bolt set description', group: 'PipeEX' },
    { id: 'p15', name: 'Pix_PPG_912.BoltSet.Diameter', category: 'Bolts', dataType: 'number', description: 'Bolt diameter', group: 'PipeEX' },
    { id: 'p16', name: 'Pix_PPG_910.BoltSet.Quantity', category: 'Bolts', dataType: 'number', description: 'Bolt quantity', group: 'PipeEX' },
    { id: 'p17', name: 'Pix_PPG_911.BoltSet.BoltLength', category: 'Bolts', dataType: 'number', description: 'Bolt length', group: 'PipeEX' },
    { id: 'p18', name: 'Pix_PPG_916.BoltSet.Material', category: 'Bolts', dataType: 'string', description: 'Bolt material', group: 'PipeEX' },
    { id: 'p19', name: 'Pix_PPG_915.BoltSet.Standard', category: 'Bolts', dataType: 'string', description: 'Bolt standard', group: 'PipeEX' },

    // General Parameters
    { id: 'p20', name: 'PiX_GN_4_ItemTag', category: 'Tags', dataType: 'string', description: 'Item tag', group: 'PipeEX' },
    { id: 'p21', name: 'PiX_06_Mass_kg_Piping', category: 'Physical', dataType: 'number', description: 'Mass in kg', group: 'PipeEX' },
    { id: 'p22', name: 'SSReference', category: 'Tags', dataType: 'string', description: 'SS Reference', group: 'PipeEX' },

    // Standard Revit Parameters
    { id: 'p23', name: 'Description', category: 'Identity Data', dataType: 'string', description: 'Element description', isReadOnly: false },
    { id: 'p24', name: 'Comments', category: 'Identity Data', dataType: 'string', description: 'Element comments', isReadOnly: false },
    { id: 'p25', name: 'Mark', category: 'Identity Data', dataType: 'string', description: 'Element mark', isReadOnly: false },
  ];
}

/**
 * Placeholder function to simulate fetching elements from Revit API
 */
async function fetchRevitElements(): Promise<RevitElement[]> {
  await new Promise(resolve => setTimeout(resolve, 100));

  const baseParameters = await fetchRevitParameters();

  return [
    {
      id: 'e1',
      name: 'Standard Pipe',
      type: 'Pipe',
      familyName: 'Pipe Types',
      category: 'Pipes',
      parameters: baseParameters.filter(p => ['Connections', 'Specifications', 'Material', 'Insulation'].includes(p.category)),
      connectors: [
        { id: 'c1', isPrimary: true, isSecondary: false, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
        { id: 'c2', isPrimary: false, isSecondary: true, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
      ]
    },
    {
      id: 'e2',
      name: '90 Degree Elbow',
      type: 'Fitting',
      familyName: 'Elbow - Generic',
      category: 'Pipe Fittings',
      parameters: baseParameters.filter(p => ['Connections', 'Specifications', 'Material'].includes(p.category)),
      connectors: [
        { id: 'c3', isPrimary: true, isSecondary: false, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
        { id: 'c4', isPrimary: false, isSecondary: true, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
      ]
    },
    {
      id: 'e3',
      name: 'Tee',
      type: 'Fitting',
      familyName: 'Tee - Generic',
      category: 'Pipe Fittings',
      parameters: baseParameters.filter(p => ['Connections', 'Specifications', 'Material'].includes(p.category)),
      connectors: [
        { id: 'c5', isPrimary: true, isSecondary: false, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
        { id: 'c6', isPrimary: false, isSecondary: true, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
        { id: 'c7', isPrimary: false, isSecondary: false, isPerpendicular: true, connectorType: 'End', shape: 'Round' },
      ]
    },
    {
      id: 'e4',
      name: 'Weld Neck Flange',
      type: 'Flange',
      familyName: 'Flange - Weld Neck',
      category: 'Pipe Fittings',
      parameters: baseParameters.filter(p => ['Connections', 'Specifications', 'Material', 'Bolts'].includes(p.category)),
      connectors: [
        { id: 'c8', isPrimary: true, isSecondary: false, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
      ]
    },
    {
      id: 'e5',
      name: 'Gate Valve',
      type: 'Valve',
      familyName: 'Valve - Gate',
      category: 'Pipe Accessories',
      parameters: baseParameters.filter(p => ['Connections', 'Specifications', 'Material', 'Tags'].includes(p.category)),
      connectors: [
        { id: 'c9', isPrimary: true, isSecondary: false, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
        { id: 'c10', isPrimary: false, isSecondary: true, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
      ]
    },
    {
      id: 'e6',
      name: 'Pipe Support',
      type: 'Support',
      familyName: 'Pipe Support - Generic',
      category: 'Pipe Accessories',
      parameters: baseParameters.filter(p => ['Tags', 'Identity Data'].includes(p.category)),
    },
    {
      id: 'e7',
      name: 'Pressure Gauge',
      type: 'Instrument',
      familyName: 'Instrument - Pressure',
      category: 'Pipe Accessories',
      parameters: baseParameters.filter(p => ['Tags', 'Identity Data', 'Connections'].includes(p.category)),
      connectors: [
        { id: 'c11', isPrimary: true, isSecondary: false, isPerpendicular: false, connectorType: 'End', shape: 'Round' },
      ]
    },
  ];
}

/**
 * Placeholder function to simulate fetching families from Revit API
 */
async function fetchRevitFamilies(): Promise<RevitFamily[]> {
  await new Promise(resolve => setTimeout(resolve, 50));

  return [
    { id: 'f1', name: 'Pipe Types', category: 'Pipes', types: ['Standard', 'Schedule 40', 'Schedule 80', 'Schedule 160'] },
    { id: 'f2', name: 'Elbow - Generic', category: 'Pipe Fittings', types: ['45 Degree', '90 Degree', '180 Degree'] },
    { id: 'f3', name: 'Tee - Generic', category: 'Pipe Fittings', types: ['Equal', 'Reducing'] },
    { id: 'f4', name: 'Flange - Weld Neck', category: 'Pipe Fittings', types: ['Class 150', 'Class 300', 'Class 600', 'Class 900', 'Class 1500'] },
    { id: 'f5', name: 'Valve - Gate', category: 'Pipe Accessories', types: ['Flanged', 'Threaded', 'Welded'] },
    { id: 'f6', name: 'Valve - Ball', category: 'Pipe Accessories', types: ['Full Port', 'Reduced Port'] },
    { id: 'f7', name: 'Pipe Support - Generic', category: 'Pipe Accessories', types: ['Hanger', 'Guide', 'Anchor', 'Shoe'] },
    { id: 'f8', name: 'Bolt Set', category: 'Pipe Accessories', types: ['Standard', 'Stud Bolt'] },
  ];
}

/**
 * Placeholder function to simulate fetching project settings from Revit API
 */
async function fetchRevitProjectSettings(): Promise<RevitProjectSettings> {
  await new Promise(resolve => setTimeout(resolve, 50));

  return {
    projectName: 'Sample Piping Project',
    projectNumber: 'PRJ-2025-001',
    revitVersion: '2025',
    lastUpdated: new Date().toISOString(),
    units: 'metric',
  };
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: RevitState = {
  parameters: [],
  elements: [],
  families: [],
  settings: null,
  isLoaded: false,
  isLoading: false,
  error: null,
  parameterCategories: [],
};

// ============================================================================
// Store Creation
// ============================================================================

export const useRevitStore = create<RevitState & RevitActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Data setters
      setParameters: (parameters) => {
        const categories = Array.from(new Set(parameters.map(p => p.category))).sort();
        set({ parameters, parameterCategories: categories });
      },

      setElements: (elements) => {
        set({ elements });
      },

      setFamilies: (families) => {
        set({ families });
      },

      updateSettings: (newSettings) => {
        const currentSettings = get().settings;
        set({
          settings: currentSettings
            ? { ...currentSettings, ...newSettings }
            : newSettings as RevitProjectSettings
        });
      },

      // Loading state actions
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error, isLoading: false });
      },

      setLoaded: (isLoaded) => {
        set({ isLoaded });
      },

      // Data loading actions
      loadRevitApiData: async () => {
        const { isLoaded, isLoading } = get();

        // Prevent duplicate loading
        if (isLoaded || isLoading) return;

        set({ isLoading: true, error: null });

        try {
          // Fetch all data in parallel
          const [parameters, elements, families, settings] = await Promise.all([
            fetchRevitParameters(),
            fetchRevitElements(),
            fetchRevitFamilies(),
            fetchRevitProjectSettings(),
          ]);

          const categories = Array.from(new Set(parameters.map(p => p.category))).sort();

          set({
            parameters,
            elements,
            families,
            settings,
            parameterCategories: categories,
            isLoaded: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load Revit data';
          set({
            error: errorMessage,
            isLoading: false,
          });
        }
      },

      refreshData: async () => {
        set({ isLoaded: false });
        await get().loadRevitApiData();
      },

      // Reset
      resetData: () => {
        set(initialState);
      },

      // Selectors/Getters
      getParametersByCategory: (category) => {
        const params = get().parameters;
        return Array.isArray(params) ? params.filter(p => p && p.category === category) : [];
      },

      getParameterByName: (name) => {
        const params = get().parameters;
        return Array.isArray(params) ? params.find(p => p && p.name === name) : undefined;
      },

      getElementsByType: (type) => {
        const elems = get().elements;
        return Array.isArray(elems) ? elems.filter(e => e && e.type === type) : [];
      },

      getFamiliesByCategory: (category) => {
        const fams = get().families;
        return Array.isArray(fams) ? fams.filter(f => f && f.category === category) : [];
      },
    }),
    {
      name: 'pipeex-revit-store',
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        // Only persist settings, not the full data (which should be reloaded from Revit)
        settings: state.settings,
      }),
    }
  )
);

// ============================================================================
// Selector Hooks (for optimized re-renders)
// ============================================================================

export const useRevitParameters = () => useRevitStore((state) => state.parameters);
export const useRevitElements = () => useRevitStore((state) => state.elements);
export const useRevitFamilies = () => useRevitStore((state) => state.families);
export const useRevitSettings = () => useRevitStore((state) => state.settings);
export const useRevitLoading = () => useRevitStore((state) => state.isLoading);
export const useRevitLoaded = () => useRevitStore((state) => state.isLoaded);
export const useRevitError = () => useRevitStore((state) => state.error);
export const useParameterCategories = () => useRevitStore((state) => state.parameterCategories);
